import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError } from '../../api/client';
import type { ModuleSelection } from '@pixerito/api-client';
import { defaultModules, ModuleToggles } from '../../components/ModuleToggles';
import { Field, Icon } from '../../components/ui';

export function generatePassword() {
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(10);
  crypto.getRandomValues(bytes);
  return 'Px-' + Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

export function NewClientPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', contactName: '', contactEmail: '', contactPhone: '', notes: '' });
  const [access, setAccess] = useState({ name: '', email: '', password: generatePassword() });
  const [modules, setModules] = useState<ModuleSelection[]>(defaultModules());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const setF = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });
  const setA = (k: keyof typeof access) => (e: React.ChangeEvent<HTMLInputElement>) => setAccess({ ...access, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setMessage(null);
    try {
      const created = await api.createClient({
        ...form,
        modules,
        access: { name: access.name || form.contactName, email: access.email || form.contactEmail, password: access.password },
      });
      navigate(`/admin/clientes/${created.id}`, {
        state: { created: { email: (access.email || form.contactEmail).trim().toLowerCase(), password: access.password } },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setMessage(err.message);
      } else setMessage('Não foi possível salvar. Tente de novo.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <Link to="/admin" className="back-link">
        <Icon name="arrowLeft" size={15} />
        Clientes
      </Link>
      <div className="page-head">
        <div className="stack" style={{ gap: 6 }}>
          <p className="eyebrow">&lt;_Novo_Cliente/&gt;</p>
          <h1 className="t-display">Cadastrar cliente</h1>
          <p className="t-sub">Dados, serviços contratados e o acesso ao painel.</p>
        </div>
      </div>

      <div className="stack-lg" style={{ maxWidth: 860 }}>
        {message && (
          <div className="notice notice--error" role="alert">
            {message}
          </div>
        )}

        <section className="card stack">
          <h2 className="t-h3">Dados do cliente</h2>
          <div className="grid-2">
            <Field label="Nome da empresa" htmlFor="c-name" error={errors.name}>
              <input
                id="c-name"
                className="input"
                value={form.name}
                onChange={setF('name')}
                aria-invalid={!!errors.name}
                placeholder="Ex.: Café Grão Dourado"
              />
            </Field>
            <Field label="Contratante" htmlFor="c-contact">
              <input
                id="c-contact"
                className="input"
                value={form.contactName}
                onChange={setF('contactName')}
                placeholder="Nome de quem contratou a Pixerito"
              />
            </Field>
            <Field label="E-mail do contratante" htmlFor="c-email" error={errors.contactEmail}>
              <input
                id="c-email"
                className="input"
                type="email"
                value={form.contactEmail}
                onChange={setF('contactEmail')}
                aria-invalid={!!errors.contactEmail}
              />
            </Field>
            <Field label="Telefone / WhatsApp" htmlFor="c-phone">
              <input
                id="c-phone"
                className="input"
                type="tel"
                value={form.contactPhone}
                onChange={setF('contactPhone')}
                placeholder="(14) 99999-0000"
              />
            </Field>
          </div>
          <Field label="Observações internas" htmlFor="c-notes" hint="Só a equipe vê.">
            <textarea id="c-notes" className="textarea" value={form.notes} onChange={setF('notes')} />
          </Field>
        </section>

        <section className="card stack">
          <div>
            <h2 className="t-h3">Serviços contratados</h2>
            <p className="muted t-small">O cliente só vê os módulos ativos. Dá para mudar depois.</p>
          </div>
          {errors.modules && <span className="field__error">{errors.modules}</span>}
          <ModuleToggles value={modules} onChange={setModules} />
        </section>

        <section className="card stack">
          <div>
            <h2 className="t-h3">Acesso ao painel</h2>
            <p className="muted t-small">Em branco, usamos o nome e o e-mail do contratante.</p>
          </div>
          <div className="grid-2">
            <Field label="Nome de quem acessa" htmlFor="a-name" error={errors['access.name']}>
              <input
                id="a-name"
                className="input"
                value={access.name}
                onChange={setA('name')}
                placeholder={form.contactName || 'Nome'}
                aria-invalid={!!errors['access.name']}
              />
            </Field>
            <Field label="E-mail de login" htmlFor="a-email" error={errors['access.email']}>
              <input
                id="a-email"
                className="input"
                type="email"
                value={access.email}
                onChange={setA('email')}
                placeholder={form.contactEmail || 'email@cliente.com.br'}
                aria-invalid={!!errors['access.email']}
              />
            </Field>
          </div>
          <Field
            label="Senha inicial"
            htmlFor="a-pass"
            error={errors['access.password']}
            hint="Mínimo de 8 caracteres. Envie para o cliente junto com o link do painel."
          >
            <div className="password-box">
              <input
                id="a-pass"
                className="input"
                value={access.password}
                onChange={setA('password')}
                aria-invalid={!!errors['access.password']}
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setAccess({ ...access, password: generatePassword() })}
              >
                Gerar outra
              </button>
            </div>
          </Field>
        </section>

        <div className="row">
          <button className="btn btn--action" type="submit" disabled={busy}>
            {busy ? 'Salvando…' : 'Cadastrar cliente'}
          </button>
          <Link to="/admin" className="btn btn--quiet">
            Cancelar
          </Link>
        </div>
      </div>
    </form>
  );
}
